import { Head, router } from "@inertiajs/react";
import { CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import AppLogoIcon from "@/components/app-logo-icon";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface SubscriptionSuccessPageProps {
  subscribed: boolean;
  subscription_status: string;
  subscription_plan: string;
}

export default function SubscriptionSuccess() {
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionActive, setSubscriptionActive] = useState(false);

  useEffect(() => {
    // Check subscription status immediately first
    const initialCheck = async () => {
      try {
        const response = await fetch("/api/user/subscription-status");
        if (response.ok) {
          const data = (await response.json()) as SubscriptionSuccessPageProps;
          if (data.subscribed) {
            setSubscriptionActive(true);
            setIsLoading(false);
            // Redirect to dashboard after a short delay
            setTimeout(() => {
              router.visit("/dashboard", { replace: true });
            }, 2000);
            return; // Don't start polling if already active
          }
        }
      } catch (error) {
        setIsLoading(false);
        console.error("Error checking subscription status:", error);
      }
    };

    const interval = setInterval(() => {
      void initialCheck();
    }, 3000);

    // Stop checking after 2 minutes (fallback)
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setIsLoading(false);
      // If subscription is not active after timeout, show error state
      if (!subscriptionActive) {
        setSubscriptionActive(false);
      }
    }, 120000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  if (isLoading) {
    return (
      <>
        <Head title="Setting up your subscription..." />

        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <Card className="w-full max-w-md text-center">
            <CardHeader>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100">
                <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
              </div>
              <CardTitle className="text-xl">Setting up your subscription...</CardTitle>
              <CardDescription>
                We&apos;re processing your payment and activating your account. This usually takes just a few seconds.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-orange-500"></div>
                  <span>Processing payment confirmation</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-orange-500"></div>
                  <span>Activating your subscription</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-orange-500"></div>
                  <span>Setting up your dashboard</span>
                </div>
              </div>

              <div className="pt-4 text-xs text-gray-500 dark:text-gray-400">
                Please don&apos;t close this window. You&apos;ll be redirected automatically.
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Show error state if subscription is not active after timeout
  if (!subscriptionActive) {
    return (
      <>
        <Head title="Subscription setup taking longer than expected" />

        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <Card className="w-full max-w-md text-center">
            <CardHeader>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
                <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <CardTitle className="text-xl">Setup taking longer than expected</CardTitle>
              <CardDescription>
                Your payment was processed successfully, but we&apos;re still waiting for the confirmation. This
                sometimes happens with international payments.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg bg-yellow-50 p-3 text-sm dark:bg-yellow-900/20">
                <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-semibold">Don&apos;t worry!</span>
                </div>
                <p className="mt-1 text-yellow-600 dark:text-yellow-300">
                  Your subscription will be activated automatically. You can refresh this page or check back in a few
                  minutes.
                </p>
              </div>

              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <p>🔄 What to do next:</p>
                <ul className="space-y-1 text-left">
                  <li>• Wait 2-3 more minutes for processing</li>
                  <li>• Refresh this page to check status</li>
                  <li>• Contact support if it takes longer than 10 minutes</li>
                </ul>
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => window.location.reload()}
                  className="w-full bg-orange-500 text-white hover:bg-orange-600"
                >
                  Refresh Status
                </Button>
                <Button variant="outline" onClick={() => router.visit("/dashboard")} className="w-full">
                  Go to Dashboard Anyway
                </Button>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400">
                Need help?{" "}
                <a
                  href="mailto:support@ogpilot.com"
                  className="text-orange-600 hover:text-orange-700 dark:text-orange-400"
                >
                  Contact us
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Head title="Subscription activated!" />

      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-xl">Welcome to OG Auto-Pilot!</CardTitle>
            <CardDescription>
              Your subscription has been activated successfully. You&apos;re now ready to manage unlimited social images
              across your domains.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <div className="rounded-lg bg-green-50 p-3 text-sm dark:bg-green-900/20">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <AppLogoIcon className="h-4 w-4" />
                  <span className="font-semibold">Account activated!</span>
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <p>✨ You now have access to:</p>
              <ul className="space-y-1 text-left">
                <li>• Domain management dashboard</li>
                <li>• Unlimited social image generations</li>
                <li>• Advanced refinement tools</li>
                <li>• Priority support</li>
              </ul>
            </div>

            <Button
              onClick={() => router.visit("/dashboard")}
              className="w-full bg-orange-500 text-white hover:bg-orange-600"
            >
              Go to Dashboard
            </Button>

            <div className="text-xs text-gray-500 dark:text-gray-400">Redirecting you automatically...</div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
